import React from 'react'
import SeoPage from '../components/SeoPage'

import Img from '../components/Image'
import { graphql, Link } from 'gatsby'

const ContentPage = ({ data, location, ...props }) => {
    return (
        <SeoPage
            leadImage={data.img0.childImageSharp.fluid.src}
            location={location}
            title="L’alcool en bateau ou embarcation sur l’eau, que dit la loi?"
            description="Découvrez en quoi consiste l’infraction de conduite avec les facultés affaiblies par l’alcool en naviguant sur un bateau!"
            fr={undefined}
            lawType="criminal"
            specialtyText="pour Alcool au volant sur l’eau">
            <p>
                Naviguer sur l’eau après avoir consommé de l’alcool peut sembler
                anodin pour certains, surtout dans un contexte de loisir.
                Pourtant, ce geste peut entraîner des conséquences sérieuses,
                dont une possible condamnation criminelle.
            </p>
            <ul>
                <li>
                    <strong>
                        Au Canada, la conduite avec les facultés affaiblies par
                        l’alcool ne se limite pas aux routes : elle s’applique
                        également sur l’eau
                    </strong>
                    .
                </li>
            </ul>
            <p>
                Que vous soyez capitaine d’un yacht ou simple conducteur d’un
                petit bateau, les règles sont les mêmes!
            </p>
            <p>
                <Img
                    fluid={data.img0.childImageSharp.fluid}
                    title="L'infraction d'alcool au volant s'applique aux bateaux!"
                    alt=""
                />
            </p>
            <table>
                <tr>
                    <td>
                        <p>Mais il est à se demander:</p>
                        <ul>
                            <li>
                                Qu’entend-on par conduite avec les facultés
                                affaiblies par l’alcool dans un contexte
                                nautique?
                            </li>
                            <li>
                                Quelles embarcations sont visées par la loi?
                            </li>
                            <li>
                                La présomption de conduite s’applique-t-elle
                                aussi aux bateaux?
                            </li>
                        </ul>
                        <p>
                            <strong>
                                Dans cet article, nous répondons à toutes ces
                                questions!
                            </strong>
                        </p>
                    </td>
                </tr>
            </table>
            <h2>
                <strong>
                    <strong>Qu’est-ce que l’infraction criminelle de </strong>
                </strong>
                conduite avec les facultés affaiblies par l’alcool dans un
                contexte nautique?
            </h2>
            <p>
                L’infraction de conduite avec les facultés affaiblies par
                l’alcool est régie par le même article{' '}
                <Link to="https://laws-lois.justice.gc.ca/fra/lois/c-46/section-320.14.html">
                    320.14
                </Link>{' '}
                du <em>Code criminel</em> pour tous les moyens de transport, y
                compris les embarcations nautiques. Selon cet article, cette
                infraction peut être retenue dans deux situations distinctes.
                Adapté au contexte nautique, cela signifie qu’il est interdit de
                :
            </p>
            <h3>
                Piloter un bateau avec les facultés affaiblies par l’alcool, une
                drogue ou une combinaison des deux
            </h3>
            <p>
                Piloter un bateau devient une infraction criminelle en vertu de
                l’article 320.14(1)a) dès que les facultés du conducteur sont
                affaiblies à un quelconque degré, par l’effet de l’alcool, d’une
                drogue ou d’une combinaison des deux. Les policiers peuvent se
                baser sur divers indices, tels que:{' '}
            </p>
            <ul>
                <li>Un comportement agité, agressif ou incohérent</li>
                <li>Une odeur d’alcool</li>
                <li>
                    Une démarche instable à bord ou au moment du débarquement
                </li>
                <li>Des troubles de coordination ou de l’élocution</li>
            </ul>
            <p>
                Lorsque ces signes ne peuvent être attribués à une autre cause
                raisonnable que la consommation d’alcool ou de drogue, les
                policiers sont en droit de conclure que la capacité de piloter
                l’embarcation est altérée.
            </p>
            <h3>
                Piloter un bateau avec un taux d’alcoolémie{' '}
                <strong>
                    <strong>égal ou supérieur à 80 mg/100 ml de sang</strong>
                </strong>
            </h3>
            <p>
                Même en l’absence de signes visibles d’affaiblissement, un
                conducteur de bateau peut être accusé en vertu de l’article
                320.14(1)b) s’il présente un taux d’alcoolémie égal ou supérieur
                à 80 mg pour 100 ml de sang, dans les deux heures suivant la
                navigation.
            </p>
            <p>
                Cependant, si la consommation d’alcool a eu lieu uniquement
                après avoir quitté l’embarcation, la personne ne peut pas être
                reconnue coupable.
            </p>
            <p>
                Il est important de noter que les policiers ont recours à des
                dispositifs tels que les alcootests ou les éthylomètres pour
                mesurer précisément le taux d’alcoolémie du conducteur. La
                validité de cette mesure dépend du respect strict de certaines
                procédures, sans quoi les résultats pourraient être contestés.
            </p>
            <h2>Quelles embarcations nautiques sont visées par la loi?</h2>
            <p>
                La conduite avec les facultés affaiblies ne concerne pas
                uniquement les automobilistes. En vertu de l’article 320.14 du{' '}
                <em>Code criminel</em>, il est interdit de conduire tout{' '}
                <strong>« moyen de transport »</strong> lorsque les facultés
                sont affaiblies par l’alcool, la drogue ou une combinaison des
                deux.{' '}
            </p>
            <p>
                Cette notion de « moyen de transport » est précisée à l’article{' '}
                <Link to="https://laws-lois.justice.gc.ca/fra/lois/c-46/section-320.11.html">
                    320.11
                </Link>
                , qui englobe notamment les véhicules à moteur, les{' '}
                <strong>bateaux</strong>, les aéronefs et le matériel
                ferroviaire.
            </p>
            <h3>
                Comment le <em>Code criminel</em> définit-il un « bateau »?
            </h3>
            <p>
                L’article 320.11 du <em>Code criminel</em> définit le terme
                bateau comme incluant notamment un aéroglisseur. En anglais, le
                mot utilisé est vessel, un terme plus large qui désigne toute
                embarcation capable de se déplacer sur l’eau.{' '}
                <strong>
                    La loi ne précise à aucun moment que le bateau doit être
                    motorisé pour être visé!
                </strong>
            </p>
            <p>
                <Img
                    fluid={data.img1.childImageSharp.fluid}
                    title="Quels bateaux sont visés par l’infraction d’alcool au volant?"
                    alt=""
                />
            </p>
            <p>
                En conséquence, la définition légale du terme bateau est très
                large. Elle englobe bien plus que les embarcations à moteur. On
                pourrait croire, à tort, que seules les embarcations motorisées
                sont concernées, comme :
            </p>
            <ul>
                <li>Les bateaux à moteur </li>
                <li>Les motomarines de type Sea-Doo</li>
                <li>Les bateaux de pêche</li>
                <li>Les voiliers munis d’un moteur auxiliaire</li>
                <li>Les yachts ou chaloupes motorisées</li>
                <li>Les bateaux pneumatiques motorisés</li>
                <li>Les traversiers ou autres embarcations commerciales</li>
            </ul>
            <p>
                <strong>Mais ce n’est pas le cas</strong>. Le{' '}
                <em>Code criminel</em> n’exige pas qu’un bateau soit motorisé
                pour être inclus dans la définition de « moyen de transport ».{' '}
            </p>
            <ul>
                <li>
                    <strong>
                        Autrement dit, une embarcation propulsée par la seule
                        force musculaire, comme un canot ou un kayak, est aussi
                        visée par la loi.
                    </strong>
                </li>
            </ul>
            <h4>L’impact du projet de loi C-46</h4>
            <p>
                Avant 2018, les bateaux et les embarcations nautiques propulsés
                exclusivement par la force humaine étaient exclus de la
                définition pour l’infraction criminelle de conduite avec les
                facultés affaiblies. Toutefois, l’adoption du projet de loi
                C-46, qui a modifié les dispositions <em>du Code criminel</em>{' '}
                en matière de conduite avec les facultés affaiblies, a supprimé
                cette exception.
            </p>
            <ul>
                <li>
                    Ce changement législatif est survenu, entre autres, à la
                    suite de recommandations du Conseil canadien de la sécurité
                    nautique, qui soulignait qu’un nombre significatif de décès
                    liés à l’alcool impliquaient des embarcations non
                    motorisées.{' '}
                </li>
            </ul>
            <p>
                <strong>
                    Des statistiques ont confirmé que les accidents causés par
                    la consommation d’alcool touchaient autant les bateaux à
                    moteur que ceux à propulsion humaine
                </strong>
                .
            </p>
            <p>
                Depuis cette réforme, toute embarcation nautique, qu’elle soit
                motorisée ou non, est considérée comme un « moyen de transport »
                au sens de l’article 320.14. Ainsi, conduire un canot, un kayak
                ou un pédalo sous l’effet de l’alcool peut entraîner des
                accusations criminelles.
            </p>
            <h3>
                Exemple jurisprudentiel démontrant qu’un bateau n’a pas besoin
                d’un moteur :{' '}
                <strong>
                    <strong>R. c. Sillars</strong>
                </strong>
            </h3>
            <p>
                La décision{' '}
                <Link to="https://www.canlii.org/en/on/onca/doc/2022/2022onca510/2022onca510.html">
                    <em>R. c. Sillars</em>
                </Link>
                , rendue par la Cour d’appel de l’Ontario en 2022, illustre de
                manière claire qu’un bateau n’a pas besoin d’être motorisé pour
                entraîner une infraction criminelle de conduite avec les
                facultés affaiblies.
            </p>
            <h4>
                <strong>
                    <strong>Faits de l’affaire :</strong>
                </strong>
            </h4>
            <ul>
                <li>
                    Le 7 avril 2017, David Sillars (l’accusé), décide d’emmener
                    en canot un enfant de huit ans sur la rivière Muskoka,
                    malgré des conditions dangereuses : eau glaciale, courant
                    fort, et avertissements de ses proches.
                </li>
                <li>
                    Peu avant l’excursion, Sillars a consommé de l’alcool et
                    fumé du cannabis.
                </li>
                <li>
                    Le canot chavire près d’une barrière de sécurité; Sillars
                    survit, mais l’enfant est emporté par le courant et décède.
                </li>
                <li>
                    À l’hôpital, le personnel médical détecte une odeur d’alcool
                    sur Sillars.
                </li>
                <li>
                    Un test d’haleine initial donne un résultat positif (« fail
                    »), conduisant à son arrestation.
                </li>
                <li>
                    Deux tests subséquents à l’Intoxilyzer révèlent des taux
                    d’alcoolémie de 97 mg/100 ml et 100 mg/100 ml.
                </li>
                <li>
                    Une analyse sanguine confirme un taux d’alcoolémie encore
                    plus élevé : 128 mg/100 ml.
                </li>
            </ul>
            <p>
                Sillars a été accusé de conduite avec les facultés affaiblies
                causant la mort, d’avoir conduit un moyen de transport avec une
                alcoolémie égale ou supérieure à 80 mg/100 ml, de conduite
                dangereuse causant la mort et de négligence criminelle.
            </p>
            <h4>Analyse et conclusion du tribunal :</h4>
            <p>
                La Cour devait notamment déterminer si un canot non motorisé
                constitue un « bateau » au sens du <em>Code criminel.</em>{' '}
            </p>
            <p>
                Le juge de première instance a répondu par l’affirmative,
                soulignant que le terme « bateau » n’est pas limité aux
                embarcations motorisées. L'absence de toute restriction
                linguistique dans la loi démontre l’intention du législateur
                d’inclure tous les types d’embarcations, peu importe leur mode
                de propulsion.
            </p>
            <p>
                <Img
                    fluid={data.img2.childImageSharp.fluid}
                    title="L’alcool au volant concerne aussi les embarcations non motorisées!"
                    alt=""
                />
            </p>
            <p>
                La Cour d’appel a confirmé cette interprétation, estimant qu’un{' '}
                <strong>
                    canot est bel et bien un « bateau » visé par les infractions
                    relatives à la conduite avec les facultés affaiblies
                </strong>
                . La Cour d’appel a ainsi validé la culpabilité de Sillars et
                confirmé la peine imposée de six ans d’emprisonnement.
            </p>
            <p>
                Cette jurisprudence démontre clairement qu’une embarcation
                propulsée uniquement par la force musculaire est considérée
                comme un véhicule au sens de l’infraction de conduite avec les
                facultés affaiblies, en vertu de l’article 320.14 du{' '}
                <em>Code criminel</em>.
            </p>
            <h2>
                La présomption de conduite s’applique-t-elle aussi aux bateaux?
            </h2>
            <p>
                Oui, la présomption de conduite s’applique à tous les moyens de
                transport visés par le <em>Code criminel</em>, y compris les
                embarcations nautiques. Elle ne se limite donc pas aux voitures.
            </p>
            <p>
                La{' '}
                <Link to="https://avocatsalcoolauvolant.ca/accusation-controle-vehicule-alcool-volant/">
                    <strong>présomption de conduite</strong>
                </Link>{' '}
                permet de tenir une personne responsable d’avoir conduit avec
                les facultés affaiblies même si le moyen de transport n’a pas
                été mis en mouvement. Il suffit que la personne ait eu la garde
                ou le contrôle de ce moyen de transport au moment des faits.
            </p>
            <ul>
                <li>
                    <strong>
                        Autrement dit, même si vous n’avez pas déplacé le
                        bateau, vous pouvez être reconnu coupable si vous en
                        aviez le contrôle ou étiez en mesure de mettre le bateau
                        en marche!
                    </strong>
                </li>
            </ul>
            <p>
                L’article{' '}
                <Link to="https://laws-lois.justice.gc.ca/fra/lois/c-46/page-49.html#docCont">
                    320.35
                </Link>{' '}
                du <em>Code criminel </em>précise que dans les poursuites
                engagées pour une infraction prévue à l’article 320.15,
                lorsqu’il est prouvé que l’accusé occupait la place ou la
                position ordinairement occupée par la personne qui conduit un
                moyen de transport, il est présumé l’avoir conduit.{' '}
            </p>
            <p>
                Ainsi, être assis au poste de pilotage d’un bateau avec les clés
                en main peut suffire pour établir cette présomption!
            </p>
            <h4>Comment repousser cette présomption de conduite?</h4>
            <p>
                Il est important de noter qu’en vertu de l’article 320.35 du{' '}
                <em>Code criminel</em>, il est également possible de repousser
                la présomption de conduite en prouvant que l’on n’occupait pas
                cette place ou cette position dans le but de mettre en mouvement
                le moyen de transport.{' '}
            </p>
            <p>
                Cette preuve peut être établie de différentes façons, notamment
                en démontrant{' '}
                <strong>
                    que le bateau n’avait plus de carburant, qu’il était hors
                    d’usage, ou encore que le véritable conducteur se trouvait à
                    proximité et a confirmé avoir conduit le bateau durant toute
                    la journée, tandis que l’accusé n’avait à aucun moment
                    l’intention de le manœuvrer.
                </strong>
            </p>
            <p>
                <strong>La </strong>présomption de conduite s’applique-t-elle
                aux embarcations sans moteur?
            </p>
            <p>
                Techniquement, oui. Le <em>Code criminel</em> ne limite pas
                cette présomption aux seuls véhicules motorisés, ce qui signifie
                qu’elle peut aussi viser les embarcations propulsées à la rame,
                comme les kayaks ou les canots.
            </p>
            <p>
                <Img
                    fluid={data.img3.childImageSharp.fluid}
                    title="Alcool au volant pour kayak et canot!"
                    alt=""
                />
            </p>
            <p>
                Même si votre embarcation est immobile ou amarrée, vous pouvez
                être reconnu coupable de conduite avec les facultés affaiblies.
                Bien que la loi ait été initialement conçue pour les véhicules
                motorisés, elle s’applique désormais à toutes les embarcations,
                y compris celles à propulsion humaine.
            </p>
            <ul>
                <li>
                    Toutefois, à ce jour, aucun jugement ni disposition légale
                    ne précise clairement dans quelles circonstances la
                    présomption s’applique à un bateau sans moteur, comme par
                    exemple être assis dans un canot en tenant une rame.
                </li>
            </ul>
            <p>
                Si vous êtes accusé dans une telle situation,{' '}
                <strong>il est essentiel de consulter rapidement un </strong>
                <Link to="https://avocatsalcoolauvolant.ca/avocat-criminel-alcool-volant/">
                    <strong>avocat spécialisé en droit criminel</strong>
                </Link>
                <strong> pour assurer votre meilleure défense</strong>. Une
                bonne représentation juridique est la clé pour vous défendre!
            </p>
            <h2>Q&amp;A : Questions sur l’alcool en bateau</h2>
            <p>
                Voici trois questions courantes avec leurs réponses sur l’alcool
                en bateau :
            </p>
            <h3>
                <strong>
                    <strong>
                        Est-il illégal de boire en conduisant un bateau?
                    </strong>
                </strong>
            </h3>
            <p>
                Au Québec, il n’est pas interdit de consommer de l’alcool en
                conduisant un bateau, qu’il soit motorisé ou non.{' '}
            </p>
            <p>
                Contrairement à la conduite automobile, boire à bord en
                naviguant est permis, à condition que vos facultés ne soient pas
                affaiblies par l’alcool. Cela signifie que tant que vous pilotez
                le bateau sans que vos capacités soient altérées et que votre
                taux d’alcool ne dépasse pas 80 mg/100 ml, la consommation
                d’alcool n’est pas prohibée.
            </p>
            <h3>
                Est-ce que l’infraction de conduite avec les facultés affaiblies
                par l’alcool s’applique au paddleboard?
            </h3>
            <p>
                Oui, techniquement, une planche à pagaie (paddleboard) peut
                également être visée par l’infraction de conduite avec les
                facultés affaiblies. Bien que la loi ne soit pas parfaitement
                explicite à ce sujet, les tribunaux canadiens pourraient
                considérer que la pratique du paddleboard constitue la conduite
                d’un « bateau » au sens du <em>Code criminel</em>.{' '}
            </p>
            <p>
                Par conséquent, la même limite d’alcoolémie s’applique que pour
                la conduite d’un véhicule motorisé. Si le taux d’alcool dans le
                sang est égal ou supérieur à 0,08 % (80 mg/100 ml), la personne
                peut être arrêtée et faire face à des accusations de conduite
                avec les facultés affaiblies.{' '}
            </p>
            <h3>
                Est-ce que les conséquences pour conduite avec les facultés
                affaiblies par l’alcool sont les mêmes au volant d’une voiture
                et d’un bateau?
            </h3>
            <p>
                <Link to="https://avocatsalcoolauvolant.ca/consequences-alcool-volant/">
                    <strong>Les conséquences</strong>
                </Link>{' '}
                prévues par le <em>Code criminel</em> pour la conduite avec les
                facultés affaiblies par l’alcool sont les mêmes, que ce soit au
                volant d’une voiture ou à la barre d’un bateau.{' '}
            </p>
            <p>
                <Img
                    fluid={data.img4.childImageSharp.fluid}
                    title="Conséquences de l’alcool au volant sur un bateau!"
                    alt=""
                />
            </p>
            <p>
                Au Québec, toute personne reconnue coupable d’une telle
                infraction fait face à des sanctions sévères, même lors d’une
                première condamnation, telles que :
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>Conséquences</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            <strong>Description</strong>
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Amende</p>
                    </td>
                    <td>
                        <p>
                            Une amende est imposée au contrevenant, et le
                            montant varie selon la concentration d’alcool
                            détectée :
                        </p>
                        <ul>
                            <li>Entre 80 et 119 mg/100 ml : 1 000 $</li>
                            <li>Entre 120 et 159 mg/100 ml : 1 500 $</li>
                            <li>
                                160 mg/100 ml et plus, ou refus de fournir un
                                échantillon : 2 000 $
                            </li>
                        </ul>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Inscription au casier judiciaire</p>
                    </td>
                    <td>
                        <p>
                            Toute condamnation entraîne l’inscription au casier
                            judiciaire du contrevenant, ce qui peut fortement
                            nuire à ses possibilités d’emploi, à ses
                            déplacements à l’étranger ainsi qu’à d’autres
                            aspects de sa vie personnelle.
                        </p>
                        <p>
                            Heureusement, il est possible de demander un{' '}
                            <Link to="https://avocatsalcoolauvolant.ca/obtenir-pardon-alcool-volant-quebec/">
                                <strong>pardon</strong>
                            </Link>
                            <strong> </strong>après plusieurs années pour
                            suspendre cette inscription.
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Interdiction de conduire</p>
                    </td>
                    <td>
                        <p>
                            Lorsqu’une personne est reconnue coupable de
                            conduite avec les facultés affaiblies, le tribunal
                            rend une ordonnance qui interdit à l’accusé de
                            conduire tout moyen de transport, quel qu’il soit,
                            pour une durée minimale d’un an.
                        </p>
                        <p>
                            Ainsi, si l’infraction a été commise à bord d’un
                            bateau, cette ordonnance ne se limite pas à la
                            navigation : la personne ne pourra plus conduire
                            aucune autre catégorie de véhicule, qu’il s’agisse
                            d’une voiture, d’un VTT, ou même d’un aéronef.{' '}
                        </p>
                        <p>
                            En cas de circonstances aggravantes, comme un taux
                            d’alcoolémie particulièrement élevé ou une conduite
                            mettant en danger la sécurité publique, le juge peut
                            étendre cette interdiction jusqu’à trois ans.
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Peine d’emprisonnement possible</p>
                    </td>
                    <td>
                        <p>
                            Bien qu’il n’y ait pas de peine minimale pour une
                            première infraction, le juge peut ordonner une
                            incarcération selon la gravité des faits, avec une
                            peine maximale pouvant atteindre 10 ans.
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Programmes de sensibilisation obligatoires</p>
                    </td>
                    <td>
                        <p>
                            Les personnes reconnues coupables doivent souvent
                            suivre des programmes spécialisés, tels que le
                            Programme d’évaluation et de réduction du risque de
                            conduite avec facultés affaiblies (PERRCCA) ou le
                            programme Alcofrein.{' '}
                        </p>
                        <p>
                            Ces formations ont pour but de réduire les risques
                            de récidive en sensibilisant davantage aux dangers
                            liés à la conduite sous l’influence de l’alcool.
                        </p>
                    </td>
                </tr>
            </table>
            <h2>
                Vous êtes accusé pour conduite avec les facultés affaiblies?
                Consultez un avocat spécialisé en droit criminel!
            </h2>
            <p>
                Si vous faites face à une accusation de conduite avec les
                facultés affaiblies alors que vous naviguiez sur un{' '}
                <Link to="https://avocatsalcoolauvolant.ca/alcool-volant-bateau/">
                    <strong>bateau</strong>
                </Link>
                , il est plus que recommandé de consulter un avocat spécialisé
                en droit criminel. Les conséquences d’une condamnation peuvent
                être sévères et lourdes!
            </p>
            <ul>
                <li>
                    <strong>
                        Ne laissez rien au hasard lorsqu’il s’agit de votre
                        défense. Que l’infraction ait été commise sur un bateau
                        à moteur ou non, faire appel à un avocat spécialisé fait
                        une réelle différence pour protéger vos droits et
                        augmenter vos chances d’être acquitté!
                    </strong>
                </li>
            </ul>
            <p>
                <Img
                    fluid={data.img5.childImageSharp.fluid}
                    title="Avocat spécialisé en alcool au volant pour bateau!"
                    alt=""
                />
            </p>
            <p>
                <strong>
                    Trouvez rapidement un avocat en droit criminel grâce à notre
                    formulaire simple et gratuit ! Une fois votre demande
                    soumise, nous vous mettons en relation avec un avocat près
                    de chez vous.
                </strong>
            </p>
            <p>
                <strong>
                    Ce service est entièrement gratuit et sans engagement, vous
                    offrant la liberté de faire un choix en toute tranquillité!
                </strong>
            </p>
        </SeoPage>
    )
}

export default ContentPage

export const pageQuery = graphql`
    query ($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
        img0: file(
            relativePath: {
                eq: "alcool-volant-bateau-embarcation-eau/bateau-alcool-volant-infraction-criminelle.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img1: file(
            relativePath: {
                eq: "alcool-volant-bateau-embarcation-eau/bateaux-infraction-alcool-volant.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img2: file(
            relativePath: {
                eq: "alcool-volant-bateau-embarcation-eau/alcool-volant-embarcation-sans-moteur.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img3: file(
            relativePath: {
                eq: "alcool-volant-bateau-embarcation-eau/alcool-volant-kayak-canot.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img4: file(
            relativePath: {
                eq: "alcool-volant-bateau-embarcation-eau/consequences-alcool-volant-bateau.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img5: file(
            relativePath: {
                eq: "alcool-volant-bateau-embarcation-eau/avocat-alcool-volant-bateau.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
    }
`
